/**
 * Desktop auto-update funnel (PE-43). `channel` and `app_version` ride on every
 * event as PostHog super properties, so update success is sliceable per release
 * channel without repeating them here.
 *
 * The funnel is Offered → Downloaded → Applied. Each step is emitted at most
 * once per app run, so the ratios read as per-session rates rather than per
 * update check (which fires every three hours and would swamp the denominator).
 */
export enum DesktopAnalyticsEvents {
  /** An update exists for this channel; carries to_version. */
  UpdateOffered = 'update_offered',
  /** The payload staged cleanly and is ready to install; carries to_version. */
  UpdateDownloaded = 'update_downloaded',
  /** Download or staging failed; carries reason. */
  UpdateFailed = 'update_failed',
  /**
   * Applying kept failing on a corrupted staged copy (usually AV/EDR). Split
   * from UpdateFailed because it is unfixable in-app and needs a manual
   * reinstall, so it should not be read as transient churn.
   */
  UpdateBlocked = 'update_blocked',
  /**
   * The install ran and the new version is the one now running; carries
   * from_version and to_version. Emitted on the launch *after* the install, so
   * it is the only confirmation an update actually took.
   *
   * Both outcome events also carry quit_reason (restart_to_update,
   * channel_switch, quit, session_end), and on Windows elevation_required,
   * user_is_admin and installer_result (not_started, started, installed).
   * installer_result is valid only when to_version's installer writes the
   * status file; an older installer always reads as not_started.
   */
  UpdateApplied = 'update_applied',
  /**
   * An install was attempted and the app came back on the old version — a
   * rolled-back or skipped installer, or a Windows shutdown that never ran it
   * (quit_reason session_end). Carries from_version, to_version and the
   * running_version.
   */
  UpdateNotApplied = 'update_not_applied',
  /**
   * The app is below the channel's recommendedVersion and the banner showed;
   * carries current_version and recommended_version. Once per app run.
   */
  UpdateRecommendedShown = 'update_recommended_shown',
  /** The user chose Later on the recommended-update banner. */
  UpdateRecommendedDismissed = 'update_recommended_dismissed',
  /**
   * The app is below the channel's minimumVersion and the blocking screen
   * showed; carries current_version and minimum_version. Once per app run.
   */
  UpdateRequiredShown = 'update_required_shown',
  /**
   * First launch on a newer version after the blocking screen showed; carries
   * previous_version, current_version and minimum_version.
   */
  UpdateRequiredCompleted = 'update_required_completed',
  /**
   * The user can't write to the installed app (usually installed as root by IT),
   * so the update check was skipped instead of triggering an admin-password prompt.
   */
  UpdatePermissionRequired = 'update_permission_required',
  /** The user chose "Update anyway" despite the missing install permission. */
  UpdatePermissionBypassed = 'update_permission_bypassed',
}
